// FILE: src/services/examCaseBlocks.js
//
// Case Studies inside Full Exam simulations (Phase 2, PR 5).
// Plan: docs/case-exam-integration-plan.md (Rev 2).
//
// Everything here is deliberately OUTSIDE the existing question engine:
// createSession/selectWeighted are untouched. When the feature is off (flag
// or config or no published cases), the builder is never called and full
// exams are byte-identical to today.
//
// Assembly model (D2: front-loaded, PMI-style):
//   questions 1..K   = the selected case blocks, back to back, each block in
//                      case_sequence order
//   questions K+1..N = independent bank questions, blueprint-balanced AFTER
//                      subtracting the cases' category contributions
//   breaks           = session-relative: Break 1 exactly at the end of the
//                      case section (lock-on-pass then seals it, matching
//                      PMI's "you will not be able to return"); remaining
//                      breaks spread evenly through the independent portion.

import { normalizeSupabaseQuestion } from './questionService';
import { supabase } from './supabaseClient';
import { getExamBreaks } from '../config/exams';

/**
 * Fetch published cases as complete blocks (RPC, migration 076).
 *
 * The RPC filters on certification + `status = 'published'` ONLY. It does NOT
 * apply fullExam.caseSection's [minQuestions, maxQuestions] window — that is
 * applied client-side in selectExamCases() below. Do not assume the returned
 * set is already size-filtered.
 */
export async function fetchExamCaseBlocks(examId) {
  const { data, error } = await supabase.rpc('get_exam_case_blocks', {
    p_certification: examId,
  });
  if (error) throw new Error(`[examCaseBlocks] get_exam_case_blocks: ${error.message}`);
  return data || [];
}

function shuffle(arr, deterministic = false) {
  if (deterministic) return [...arr];
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Largest-remainder apportionment of `total` slots across weighted keys. */
export function apportion(weights, total) {
  const entries = Object.entries(weights).filter(([, w]) => w > 0);
  const weightSum = entries.reduce((s, [, w]) => s + w, 0);
  if (weightSum <= 0 || total <= 0) return {};
  const exact = entries.map(([k, w]) => ({ k, x: (w / weightSum) * total }));
  const counts = Object.fromEntries(exact.map(({ k, x }) => [k, Math.floor(x)]));
  let assigned = Object.values(counts).reduce((s, n) => s + n, 0);
  const byRemainder = exact
    .map(({ k, x }) => ({ k, r: x - Math.floor(x) }))
    .sort((a, b) => b.r - a.r);
  for (let i = 0; assigned < total; i += 1, assigned += 1) {
    counts[byRemainder[i % byRemainder.length].k] += 1;
  }
  return counts;
}

/**
 * Cases eligible for a sim: published (the RPC guarantees that) + question count
 * inside fullExam.caseSection's [minQuestions, maxQuestions] window.
 *
 * THIS is where the size window is enforced — not in get_exam_case_blocks. The
 * window is a PM Pixel design assumption with no PMI source; see the caseSection
 * comment in config/exams.js before changing it.
 */
export function selectExamCases(cases, caseSection, deterministic = false) {
  const { count, minQuestions, maxQuestions } = caseSection;
  const eligible = (cases || []).filter(
    (c) => c.question_count >= minQuestions && c.question_count <= maxQuestions
  );
  return shuffle(eligible, deterministic).slice(0, Math.max(0, count));
}

/** Category contributions of the selected cases' questions. */
export function caseCategoryContributions(selectedCases) {
  const out = {};
  selectedCases.forEach((c) => (c.questions || []).forEach((q) => {
    const cat = q.category || 'Unclassified';
    out[cat] = (out[cat] || 0) + 1;
  }));
  return out;
}

/**
 * Blueprint-honoring counts for the independent remainder: apportion the full
 * blueprint over the total, subtract case contributions (clamped at 0), then
 * re-apportion any clamp surplus so the remainder count is hit exactly.
 */
export function remainingCategoryCounts(distribution, contributions, total, remainder) {
  const targets = apportion(distribution, total);
  const remaining = {};
  Object.entries(targets).forEach(([cat, n]) => {
    remaining[cat] = Math.max(0, n - (contributions[cat] || 0));
  });
  let sum = Object.values(remaining).reduce((s, n) => s + n, 0);
  if (sum === remainder) return remaining;
  if (sum > remainder) {
    // Trim from the largest buckets first.
    const order = Object.keys(remaining).sort((a, b) => remaining[b] - remaining[a]);
    let over = sum - remainder;
    for (let i = 0; over > 0; i = (i + 1) % order.length) {
      if (remaining[order[i]] > 0) { remaining[order[i]] -= 1; over -= 1; }
    }
    return remaining;
  }
  // sum < remainder (cases over-served some category beyond its target):
  // hand the shortfall back proportionally to the blueprint.
  const topUp = apportion(distribution, remainder - sum);
  Object.entries(topUp).forEach(([cat, n]) => { remaining[cat] = (remaining[cat] || 0) + n; });
  return remaining;
}

/** Sample independents per category with round-robin backfill, then shuffle. */
export function sampleIndependents(pool, counts, excludeIds, deterministic = false) {
  const exclude = excludeIds || new Set();
  const byCat = new Map();
  pool.forEach((q) => {
    if (exclude.has(q.id) || exclude.has(String(q.id))) return;
    const cat = q.category || 'Unclassified';
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(q);
  });
  const picked = [];
  const leftovers = [];
  Object.entries(counts).forEach(([cat, n]) => {
    const bucket = shuffle(byCat.get(cat) || [], deterministic);
    picked.push(...bucket.slice(0, n));
    leftovers.push(...bucket.slice(n));
    byCat.delete(cat);
  });
  byCat.forEach((bucket) => leftovers.push(...bucket));
  const want = Object.values(counts).reduce((s, n) => s + n, 0);
  if (picked.length < want) {
    picked.push(...shuffle(leftovers, deterministic).slice(0, want - picked.length));
  }
  return shuffle(picked, deterministic);
}

/** Session-relative breaks: Break 1 seals the case section (D2). */
export function computeSessionBreaks(examId, caseQuestionCount, totalQuestions) {
  const base = getExamBreaks(examId);
  if (!base.length || caseQuestionCount <= 0) return null;
  const independent = totalQuestions - caseQuestionCount;
  const n = base.length;
  return base.map((b, i) => ({
    afterQuestion: i === 0
      ? caseQuestionCount
      : caseQuestionCount + Math.ceil((independent * i) / n),
    durationMinutes: b.durationMinutes,
  }));
}

/**
 * Build a full exam with front-loaded case blocks.
 * Returns { questions, caseBlocks, breaks, stats } or null when no eligible
 * cases exist (caller falls back to the standard createSession path).
 */
export function buildFullExamWithCases(rawPool, config, cases, options = {}) {
  const { excludeIds = new Set(), deterministic = false } = options;
  // Defense in depth: the independents pool must never contain case-linked
  // rows (those arrive via `cases`) or interactive types — the bank store
  // may now include both for the Question Editor's benefit (PR B).
  const pool = rawPool.filter(
    (q) => !q.caseStudyId && q.type !== 'enhanced_matching' && q.type !== 'ordered_sequence'
  );
  const caseSection = config.fullExam && config.fullExam.caseSection;
  if (!caseSection || !caseSection.count) return null;

  const selected = selectExamCases(cases, caseSection, deterministic);
  if (selected.length === 0) return null;

  const total = config.fullExam.totalQuestions;

  // 1. Case section (questions 1..K), each block in case_sequence order.
  const caseQuestions = [];
  const caseBlocks = {};
  selected.forEach((c) => {
    const startIndex = caseQuestions.length;
    const ordered = [...(c.questions || [])].sort((a, b) => a.case_sequence - b.case_sequence);
    ordered.forEach((row) => {
      const q = normalizeSupabaseQuestion(row, config.examId);
      caseQuestions.push({
        ...q,
        caseId: c.id,
        caseTitle: c.title,
        caseSlug: c.slug,
        caseSequence: row.case_sequence,
        caseStageSequence: row.case_stage_sequence,
        caseBlockStart: startIndex,
        caseBlockEnd: startIndex + ordered.length - 1,
      });
    });
    caseBlocks[c.id] = {
      id: c.id, slug: c.slug, title: c.title, learnerRole: c.learner_role,
      stages: c.stages || [],
      startIndex, endIndex: startIndex + ordered.length - 1,
      questionCount: ordered.length,
    };
  });
  const K = caseQuestions.length;

  // 2. Blueprint-balanced independents around the cases.
  const counts = remainingCategoryCounts(
    config.categoryDistribution || {}, caseCategoryContributions(selected), total, total - K);
  const caseQuestionIds = new Set(caseQuestions.map((q) => String(q.id)));
  const exclude = new Set([...excludeIds].map(String));
  caseQuestionIds.forEach((id) => exclude.add(id));
  const independents = sampleIndependents(pool, counts, exclude, deterministic);

  const questions = [...caseQuestions, ...independents];
  return {
    questions,
    caseBlocks,
    breaks: computeSessionBreaks(config.examId, K, questions.length),
    stats: { caseCount: selected.length, caseQuestionCount: K, independentCount: independents.length },
  };
}
